import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { Box, Button, Table, TableBody } from "@mui/material"
import { TreeToolbar } from "slot-definitions"
import { useDialog } from "lib/@hooks/useDialog"
import Iconify from "minimals-template/components/Iconify"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { AuthoringUserRow } from "authoring/users/authoring-user-row"
import { createAuthoringUser } from "authoring/users/controller/create-authoring-user"
import { CreateAuthoringUserDialog } from "authoring/users/components/create-authoring-user-dialog"
import { getPagedAuthoringUsers } from "authoring/users/controller/get-paged-authoring-users.api"
import { Bound } from "lib/@components/binding/Bound"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useReferenceState } from "lib/@hooks/use-reference"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useState } from "react"
import { Frag } from "lib/@components/slot/frag"
import { updateUserRoles } from "lib/permissions/update-user-roles"
import { infoSnackbar } from "lib/snackbar/info-snackbar"
import { inviteClientUser } from "routes/client-admin/plugins/users/controller/user-profiles"
import { getAuthoringGlobalRoles } from "authoring/lib/client-user/controller/get-authoring-global-roles"
import { getUserProjects } from "authoring/users/projects/controller/get-user-projects"
import { addUserToProject } from "authoring/users/controller/add-user-to-project"
import { removeUserFromProject } from "authoring/users/controller/remove-user-from-project"
import { hasDemand } from "lib/authorization/has-demand"
import { AUTHORING_MANAGE_WORK_PACKAGE_USERS_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function AuthoringUserList() {
    const [take, setTake] = useReferenceState("authoring-user-list", 10)
    const [search] = useSearchParam("search")
    const [skip, setSkip] = useState(0)
    const createNewClientUser = useDialog(<CreateAuthoringUserDialog />, {
        maxWidth: "md",
    })
    const globalAuthoringRoles = getAuthoringGlobalRoles.useResults()
    const authoringUsers = getPagedAuthoringUsers.useResults({ skip, take, searchTerm: search })
    const allProjects = getUserProjects.useResults()
    const { totalCount, edges: users } = authoringUsers ?? {}
    const canManageWorkPackageUsers = hasDemand(AUTHORING_MANAGE_WORK_PACKAGE_USERS_DEMAND)
    const projects = allProjects?.projects ?? []

    if (!authoringUsers) return null
    if (!globalAuthoringRoles) return null
    return (
        <Bound roles={globalAuthoringRoles} projects={projects}>
            <TreeToolbar.Plug>
                <Box>
                    <Button
                        color="primary"
                        onClick={handleAddUser}
                        startIcon={<Iconify icon="mdi:user-add" />}
                        variant="contained"
                        data-cy="client-admin-add-user-button"
                    >
                        Register User
                    </Button>
                </Box>
            </TreeToolbar.Plug>
            <Box mt={1}>
                <SearchBoxUsingSearchParam sx={{ flex: 1, mr: 3, ml: 3 }} placeholder="Search for users" />

                <Table size="small">
                    <TableBody>
                        <RequestPageRepeat
                            pageSize={take}
                            setSkip={setSkip}
                            total={totalCount}
                            list={users}
                            setTake={setTake}
                            item={
                                <AuthoringUserRow
                                    onUpdatePermissions={handleGlobalRoles}
                                    workPackageSelector={canManageWorkPackageUsers}
                                    onUpdateWorkPackages={handleWorkPackageUpdate}
                                />
                            }
                            ContentWrapper={Frag}
                        />
                    </TableBody>
                </Table>
            </Box>
        </Bound>
    )

    async function handleGlobalRoles(user, updatedRoles) {
        try {
            const response = await updateUserRoles({ id: user.id, roles: updatedRoles })
            if (response === true) {
                infoSnackbar("User role updated")
            } else {
                errorSnackbar("Unable to update user role")
            }
        } catch (error) {
            errorSnackbar("Unable to update user role")
        }
    }

    async function handleWorkPackageUpdate(user, projectId, isAssigned) {
        try {
            if (isAssigned) {
                await removeUserFromProject({ userId: user.id, projectId })
            } else {
                await addUserToProject({ userId: user.id, projectId })
            }
        } catch (error) {
            errorSnackbar(error?.message ?? "Unable to update work package assignment")
        }
    }

    async function handleAddUser() {
        const newUser = await createNewClientUser()
        if (newUser) {
            try {
                await createAuthoringUser({ ...newUser })
                successSnackbar("User created")
                await inviteClientUser(newUser)
                successSnackbar("Invite sent")
            } catch (error) {
                console.error(error)
                errorSnackbar(error.toString())
            }
        }
    }
}

export function AuthoringUsers() {
    return (
        <PageBreadcrumbContainer title={"Users"}>
            <Box flex={1}>
                <AuthoringUserList />
            </Box>
        </PageBreadcrumbContainer>
    )
}
